export const APPLICATION_FINALIZED_CODE = 'APPLICATION_FINALIZED';

export const APPLICATION_FINALIZED_FALLBACK_MESSAGE =
  'This application has already been signed or paid. No further changes can be made. Please contact support if you need assistance.';

// Shown when the read-only pre-check (isApplicationGroupFinalized) could not be
// completed at all — a network blip, a backend error. The action is still
// blocked (fail closed: we must not let an already-signed applicant back into
// the application), but the copy must not claim a finalized state we never
// confirmed, and the banner's Refresh button gives the way out.
export const APPLICATION_FINALIZED_CHECK_FAILED_MESSAGE =
  'We could not verify whether this quote can still be edited. Please refresh the page and try again.';

interface GraphQLErrorLike {
  message?: string;
  extensions?: { code?: string };
}

function findFinalizedError(
  errors: GraphQLErrorLike[] | undefined
): GraphQLErrorLike | undefined {
  return errors?.find((e) => e.extensions?.code === APPLICATION_FINALIZED_CODE);
}

// Returns the backend-provided message (which differs for "signed" vs "paid")
// when the error is an APPLICATION_FINALIZED error, otherwise undefined.
export function getApplicationFinalizedMessage(
  error: unknown
): string | undefined {
  if (!error || typeof error !== 'object') return undefined;

  // graphql-request ClientError: error.response.errors[].extensions.code
  const responseErrors = (error as {
    response?: { errors?: GraphQLErrorLike[] };
  }).response?.errors;
  const fromResponse = findFinalizedError(responseErrors);
  if (fromResponse)
    return fromResponse.message ?? APPLICATION_FINALIZED_FALLBACK_MESSAGE;

  // Apollo Client ApolloError: error.graphQLErrors[].extensions.code
  const graphQLErrors = (error as { graphQLErrors?: GraphQLErrorLike[] })
    .graphQLErrors;
  const fromApollo = findFinalizedError(graphQLErrors);
  if (fromApollo)
    return fromApollo.message ?? APPLICATION_FINALIZED_FALLBACK_MESSAGE;

  return undefined;
}

export function isApplicationFinalizedError(error: unknown): boolean {
  return getApplicationFinalizedMessage(error) !== undefined;
}
